import React, { useEffect, useRef, useState, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import '@schoolmedia/navbar';
import { Logout } from '../store/actions/AuthActions';
import { HOSTNAME } from '../constant';
import { ThemeContext } from '../context/ThemeContext';
import axiosInstance from '../services/AxiosInstance';
import { getStorageQuotaDetail } from '../services/StorageService';

/**
 * Helper untuk format ukuran byte menjadi format terstruktur (MB, GB, dll.)
 */
const formatBytes = (bytes, decimals = 1) => {
  if (!bytes || bytes === 0 || isNaN(bytes)) return '0 B';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

/**
 * Helper untuk normalisasi item kategori penyimpanan
 */
const formatCategoryItem = (item) => {
  if (!item) return undefined;
  if (typeof item === 'object') {
    return {
      files: item.files ?? item.count ?? item.total_files ?? 0,
      bytes: item.bytes ?? item.size ?? item.used_bytes ?? 0,
      bytes_formatted:
        item.bytes_formatted ||
        item.size_formatted ||
        item.used_formatted ||
        (typeof (item.bytes ?? item.size) === 'number'
          ? formatBytes(item.bytes ?? item.size)
          : undefined),
    };
  }
  return undefined;
};

/**
 * Helper untuk mengekstrak dan memformat data kapasitas penyimpanan (drive/storage)
 * dari response endpoint `me` (Redux `authUser` atau data `me`).
 */
export const extractDriveUsageFromMe = (userData) => {
  if (!userData || typeof userData !== 'object') return null;

  const raw =
    userData.storage ||
    userData.drive ||
    userData.drive_usage ||
    userData.storage_quota ||
    userData.quota ||
    userData.detail?.storage ||
    userData.detail?.drive ||
    userData.detail?.drive_usage ||
    userData.detail?.quota;

  if (!raw || typeof raw !== 'object') return null;

  const usedFormatted =
    raw.used_formatted ||
    raw.used_bytes_formatted ||
    raw.used_str ||
    (raw.used ? String(raw.used) : null) ||
    (typeof raw.used_bytes === 'number' ? formatBytes(raw.used_bytes) : null);

  const totalFormatted =
    raw.total_formatted ||
    raw.quota_bytes_formatted ||
    raw.quota_formatted ||
    raw.total_str ||
    (raw.total ? String(raw.total) : null) ||
    (raw.quota ? String(raw.quota) : null) ||
    (typeof raw.quota_bytes === 'number' ? formatBytes(raw.quota_bytes) : null) ||
    (typeof raw.total_bytes === 'number' ? formatBytes(raw.total_bytes) : null);

  const quotaBytes =
    raw.quota_bytes ||
    raw.total_bytes ||
    (typeof raw.quota === 'number' ? raw.quota : undefined) ||
    (typeof raw.total === 'number' ? raw.total : undefined);

  const usedBytes =
    raw.used_bytes ||
    (typeof raw.used === 'number' ? raw.used : undefined);

  let percent = undefined;
  if (typeof raw.percent === 'number') {
    percent = raw.percent;
  } else if (typeof raw.usage_percent === 'number') {
    percent = Math.round(raw.usage_percent * 100) / 100;
  } else if (typeof usedBytes === 'number' && typeof quotaBytes === 'number' && quotaBytes > 0) {
    percent = Math.round((usedBytes / quotaBytes) * 10000) / 100;
  }

  const rawDetails = raw.details || raw.breakdown || raw.kategori || raw.categories || null;
  let details = undefined;
  if (rawDetails && typeof rawDetails === 'object') {
    details = {
      image: formatCategoryItem(rawDetails.image || rawDetails.gambar),
      video: formatCategoryItem(rawDetails.video),
      audio: formatCategoryItem(rawDetails.audio),
      document: formatCategoryItem(rawDetails.document || rawDetails.dokumen),
      other: formatCategoryItem(rawDetails.other || rawDetails.lainnya),
    };
  }

  if (usedFormatted || totalFormatted || details || quotaBytes !== undefined) {
    return {
      used: usedFormatted || '0 MB',
      total: totalFormatted || '20.5 GB',
      ...(percent !== undefined ? { percent } : {}),
      ...(typeof quotaBytes === 'number' ? { quota_bytes: quotaBytes } : {}),
      ...(typeof usedBytes === 'number' ? { used_bytes: usedBytes } : {}),
      ...(details ? { details } : {}),
    };
  }

  return null;
};

export const SchoolMediaNav = ({
  user = null,
  appTitle = 'Tes',
  activeApp = 'app-testonline',
  theme = null,
  searchPlaceholder = 'Cari ujian, bank soal...',
  logoHref = 'https://schoolmedia.id',
  brandUrl = '/',
  brandLogoUrl = undefined,
  ssoEndpoint = null,
  homeUrl = '/',
  imageBrand = '/SchoolmediaTes.png',
  pembelajaranUrl = undefined,
  pembelajaranLabel = undefined,
  bursaUrl = undefined,
  bursaLabel = undefined,
  navItems = undefined,
  searchEndpoint = '/ujian',
  searchParam = 'search',
  searchItemLabel = 'ujian',
  searchEmptyText = undefined,
  searchApiEndpoint = undefined,
  searchResults = undefined,
  portalUrl = 'https://schoolmedia.id',
  helpUrl = 'https://help.schoolmedia.id',
  scoinBalance = undefined,
  scoinUrl = 'https://sbiz.co.id/',
  driveUsage: driveUsageProp = undefined,
}) => {
  const navRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const themeCtx = useContext(ThemeContext);

  const savedTheme = typeof window !== 'undefined' ? localStorage.getItem('theme') : null;
  const currentTheme = theme || themeCtx?.background?.value || savedTheme || 'light';

  const authUser = useSelector((state) => state.auth?.auth);
  const [saldo, setSaldo] = useState(null);
  const [driveUsage, setDriveUsage] = useState(null);

  const isAuth = Boolean(authUser?.id || authUser?.name || authUser?.email);

  // Ambil saldo Scoin nyata jika user login
  useEffect(() => {
    if (isAuth && scoinBalance === undefined) {
      axiosInstance
        .get('scoin/saldo')
        .then((res) => {
          if (res?.data) {
            const raw = res.data.data ?? res.data.saldo ?? res.data;
            const amount = typeof raw === 'object' ? (raw.amount ?? raw.saldo ?? 0) : Number(raw);
            setSaldo(isNaN(amount) ? 0 : amount);
          }
        })
        .catch(() => setSaldo(0));
    }
  }, [isAuth, authUser, scoinBalance]);

  // Ambil kuota drive storage nyata upstream jika user login (UUID diambil langsung dari data 'me')
  useEffect(() => {
    if (driveUsageProp !== undefined) return;

    // 1. Coba ekstrak data storage dari `authUser` jika sudah ada di Redux
    const parsedFromAuth = extractDriveUsageFromMe(authUser);
    if (parsedFromAuth) {
      setDriveUsage(parsedFromAuth);
    }

    // 2. Ambil UUID tenant dari data login endpoint me
    const userUuid =
      authUser?.uuid ||
      authUser?.detail?.uuid ||
      authUser?.tenant_id ||
      localStorage.getItem('uuid') ||
      localStorage.getItem('user_uuid') ||
      (() => {
        try {
          const raw = localStorage.getItem('userDetails');
          if (!raw) return null;
          const parsed = JSON.parse(raw);
          return parsed?.uuid || parsed?.detail?.uuid || parsed?.data?.uuid || null;
        } catch (e) {
          return null;
        }
      })();

    if (userUuid) {
      getStorageQuotaDetail(userUuid)
        .then((res) => {
          const quota = res?.data?.data || res?.data || res;
          if (quota && (quota.used_bytes_formatted || quota.quota_bytes_formatted || quota.details)) {
            setDriveUsage({
              used: quota.used_bytes_formatted || '0 B',
              total: quota.quota_bytes_formatted || '0 B',
              percent: typeof quota.usage_percent === 'number' ? Math.round(quota.usage_percent * 100) / 100 : 0,
              quota_bytes: quota.quota_bytes || 0,
              used_bytes: quota.used_bytes || 0,
              details: quota.details || null,
            });
          }
        })
        .catch((err) => {
          console.error('Gagal mengambil kuota storage:', err);
        });
    }
  }, [authUser, driveUsageProp]);

  const getUserPhoto = (u) => {
    if (!u) return null;
    if (u.foto_profil && typeof u.foto_profil === 'string' && u.foto_profil.startsWith('http')) {
      return u.foto_profil;
    }
    if (u.avatar) return u.avatar;
    if (u.photo) return u.photo;
    if (u.picture) return u.picture;
    if (u.profile_photo_url) return u.profile_photo_url;

    const profile = u.detail?.profile || u.profile || {};
    if (profile.foto_profil) {
      if (typeof profile.foto_profil === 'string' && profile.foto_profil.startsWith('http')) {
        return profile.foto_profil;
      }
      const website = profile.institusi?.website || u.detail?.institusi?.website;
      const groupCode = u.group_code || 'SI';
      if (website) {
        return `https://s3.schoolmedia.id/00-universal/${website}/${groupCode}/${profile.foto_profil}`;
      }
      return profile.foto_profil;
    }
    return null;
  };

  // Format profil user untuk web component @schoolmedia/navbar
  const currentUser =
    user !== null
      ? user
      : isAuth
      ? {
          name: authUser.name || 'User',
          email: authUser.email || `${authUser.username || 'user'}@schoolmedia.id`,
          avatar: getUserPhoto(authUser),
          role: authUser.group_code === 'GR' ? 'Guru' : authUser.group_code === 'SI' ? 'Siswa' : 'Peserta',
          group_code: authUser.group_code || 'PU',
        }
      : null;

  const effectiveBrandUrl = brandUrl || homeUrl;
  const effectiveLogoHref = logoHref || portalUrl;
  const finalDriveUsage = driveUsageProp !== undefined ? driveUsageProp : driveUsage;

  const effectiveBrandImage = brandLogoUrl || imageBrand;

  const ssoAuthUrl =
    ssoEndpoint ||
    `${HOSTNAME}/auth?redirect=${encodeURIComponent(typeof window !== 'undefined' ? window.location.href : '')}`;

  useEffect(() => {
    const el = navRef.current;
    if (!el) return;

    // Set properties dinamis (Object / Array)
    el.user = currentUser;

    if (effectiveBrandImage) {
      el.brandImage = effectiveBrandImage;
      el.brandLogo = effectiveBrandImage;
      el.brandLogoUrl = effectiveBrandImage;
      el.imageBrand = effectiveBrandImage;
    }

    if (finalDriveUsage) {
      try {
        el.driveUsage = finalDriveUsage;
        if (typeof el.requestUpdate === 'function') {
          el.requestUpdate();
        }
      } catch (err) {}
    }
    if (navItems) {
      try {
        el.navItems = typeof navItems === 'string' ? JSON.parse(navItems) : navItems;
      } catch (err) {}
    }
    if (searchResults) {
      el.searchResults = searchResults;
    }

    const handleNavigate = (e) => {
      const path = e.detail?.path;
      if (!path) return;
      if (path.startsWith('http://') || path.startsWith('https://')) {
        window.location.href = path;
      } else {
        navigate(path);
      }
    };

    const handleBrandClick = (e) => {
      const targetUrl = e.detail?.targetUrl || effectiveBrandUrl;
      if (!targetUrl) return;
      if (targetUrl.startsWith('http://') || targetUrl.startsWith('https://')) {
        window.location.href = targetUrl;
      } else {
        navigate(targetUrl);
      }
    };

    const handlePortalClick = (e) => {
      const targetUrl = e.detail?.targetUrl || effectiveLogoHref;
      if (!targetUrl) return;
      window.open(targetUrl, '_blank', 'noopener,noreferrer');
    };

    const handleDriveClick = (e) => {
      // Event on-drive-click ditangani di dalam Shadow DOM untuk popover
    };

    const handleScoinClick = (e) => {
      const targetUrl = e.detail?.targetUrl || scoinUrl || 'https://sbiz.co.id/';
      window.open(targetUrl, '_blank', 'noopener,noreferrer');
    };

    const handleLogout = () => {
      if (dispatch) {
        dispatch(Logout(navigate));
      } else {
        window.location.href = `${HOSTNAME}/auth/logout?redirect=${encodeURIComponent(window.location.origin)}`;
      }
    };

    const handleLoginClick = (e) => {
      if (e?.preventDefault) e.preventDefault();
      window.location.href = e.detail?.ssoUrl || ssoAuthUrl;
    };

    const handleThemeChange = (e) => {
      const newTheme = e.detail?.theme || (currentTheme === 'dark' ? 'light' : 'dark');
      try {
        localStorage.setItem('theme', newTheme);
      } catch (err) {}
      if (themeCtx?.changeBackground) {
        themeCtx.changeBackground({
          value: newTheme,
          label: newTheme === 'dark' ? 'Dark' : 'Light',
        });
      } else {
        const bodyEl = document.querySelector('body');
        if (bodyEl) bodyEl.setAttribute('data-theme-version', newTheme);
        if (typeof document !== 'undefined' && document.documentElement) {
          document.documentElement.setAttribute('data-theme-version', newTheme);
          if (newTheme === 'dark') {
            document.documentElement.classList.add('dark');
          } else {
            document.documentElement.classList.remove('dark');
          }
        }
      }
    };

    const handleSearchSubmit = (e) => {
      if (e?.preventDefault) e.preventDefault();
      const q = (e.detail?.query || '').trim();
      if (q) {
        navigate(`${searchEndpoint}?${searchParam}=${encodeURIComponent(q)}`);
      } else {
        navigate(searchEndpoint);
      }
    };

    const handleSearchItemClick = (e) => {
      const item = e.detail?.item;
      if (item?.url) {
        if (item.url.startsWith('http://') || item.url.startsWith('https://')) {
          window.location.href = item.url;
        } else {
          navigate(item.url);
        }
      }
    };

    el.addEventListener('on-brand-click', handleBrandClick);
    el.addEventListener('on-portal-click', handlePortalClick);
    el.addEventListener('on-navigate', handleNavigate);
    el.addEventListener('on-drive-click', handleDriveClick);
    el.addEventListener('on-scoin-click', handleScoinClick);
    el.addEventListener('on-logout', handleLogout);
    el.addEventListener('on-login-click', handleLoginClick);
    el.addEventListener('on-theme-change', handleThemeChange);
    el.addEventListener('on-search-submit', handleSearchSubmit);
    el.addEventListener('on-search-item-click', handleSearchItemClick);

    return () => {
      el.removeEventListener('on-brand-click', handleBrandClick);
      el.removeEventListener('on-portal-click', handlePortalClick);
      el.removeEventListener('on-navigate', handleNavigate);
      el.removeEventListener('on-drive-click', handleDriveClick);
      el.removeEventListener('on-scoin-click', handleScoinClick);
      el.removeEventListener('on-logout', handleLogout);
      el.removeEventListener('on-login-click', handleLoginClick);
      el.removeEventListener('on-theme-change', handleThemeChange);
      el.removeEventListener('on-search-submit', handleSearchSubmit);
      el.removeEventListener('on-search-item-click', handleSearchItemClick);
    };
  }, [
    currentUser,
    navigate,
    dispatch,
    ssoAuthUrl,
    currentTheme,
    themeCtx,
    finalDriveUsage,
    searchEndpoint,
    searchParam,
    effectiveBrandUrl,
    effectiveLogoHref,
    scoinUrl,
    navItems,
    searchResults,
    effectiveBrandImage,
  ]);

  // Ukur tinggi navbar secara akurat agar header & sidebar benar-benar rapat tanpa celah
  useEffect(() => {
    const updateHeight = () => {
      const el = navRef.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        const height = Math.round(rect.height) || el.offsetHeight;
        if (height > 0) {
          document.documentElement.style.setProperty('--sm-navbar-height', `${height}px`);
        }
      }
    };

    updateHeight();

    let ro;
    if (typeof ResizeObserver !== 'undefined' && navRef.current) {
      ro = new ResizeObserver(() => {
        updateHeight();
      });
      ro.observe(navRef.current);
    }

    window.addEventListener('resize', updateHeight);
    return () => {
      window.removeEventListener('resize', updateHeight);
      if (ro) ro.disconnect();
    };
  }, []);

  const scoinBalanceStr =
    scoinBalance !== undefined
      ? String(scoinBalance)
      : saldo !== null
      ? Number(saldo).toLocaleString('id-ID')
      : '0';

  return (
    <app-navbar
      ref={navRef}
      active-app={activeApp}
      app-title={appTitle}
      active-path={location.pathname}
      brand-url={effectiveBrandUrl}
      brand-logo-url={effectiveBrandImage}
      brand-logo={effectiveBrandImage}
      brand-image={effectiveBrandImage}
      logo-href={effectiveLogoHref}
      portal-url={portalUrl}
      dashboard-url={homeUrl}
      pembelajaran-url={pembelajaranUrl}
      pembelajaran-label={pembelajaranLabel}
      bursa-url={bursaUrl}
      bursa-label={bursaLabel}
      nav-items={navItems ? (typeof navItems === 'string' ? navItems : JSON.stringify(navItems)) : undefined}
      search-endpoint={searchEndpoint}
      search-api-endpoint={searchApiEndpoint}
      search-param={searchParam}
      search-placeholder={searchPlaceholder}
      search-item-label={searchItemLabel}
      search-empty-text={searchEmptyText}
      search-results={searchResults ? JSON.stringify(searchResults) : undefined}
      sso-endpoint={ssoAuthUrl}
      help-url={helpUrl}
      scoin-balance={scoinBalanceStr}
      scoin-url={scoinUrl}
      drive-usage={finalDriveUsage ? JSON.stringify(finalDriveUsage) : undefined}
      user={currentUser ? JSON.stringify(currentUser) : undefined}
      theme={currentTheme}
    />
  );
};

export default SchoolMediaNav;
